import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { listTablePrefs, TablePrefsParams } from 'app/constants'
import { pick, prop, keys } from 'ramda'
import DocumentMeta from 'core/components/DocumentMeta'
import DataKeys from 'k8s/DataKeys'
import { ArrayElement } from 'core/actions/Action'
import { GridViewColumn } from 'core/elements/grid/Grid'
import { listImages, deleteImage, testHostGlance, getHostGlanceEndpoint } from './actions'
import { listVirtualMachinesLite } from 'openstack/components/vms/actions'
import { listVolumeSnapshots } from 'openstack/components/storage/snapshots/actions'
import Tabs from 'core/elements/tabs'
import Tab from 'core/elements/tabs/Tab'
import ListContainer from 'core/containers/ListContainer'
import useListAction from 'core/hooks/useListAction'
import { createUsePrefParamsHook } from 'core/hooks/useParams'
import useGlobalParams from 'core/hooks/useGlobalParams'
import { imagesSelector } from './selectors'
import { virtualMachinesLiteByIdSelector } from 'openstack/components/vms/selectors'
import { volumeSnapshotsByIdSelector } from 'openstack/components/storage/snapshots/selectors'
import InferActionParams from 'core/actions/InferActionParams'
import { useAppSelector } from 'app/store'
import useReactRouter from 'use-react-router'
import { durationBetweenDates } from 'utils/misc'
import { humanReadableSize } from 'openstack/helpers'
import { GridBatchActionSpec } from 'core/elements/grid/hooks/useGridSelectableRows'
import { createGridLinkCell } from 'core/elements/grid/cells/GridLinkCell'
import EditImageModal from './EditImageModal'
import EditImageMetadataModal from './EditImageMetadataModal'
import { createResourceLabelsCell } from 'k8s/components/common/entity/labels-and-annotations/helpers'
import { routeManager } from 'core/utils/routes'
import ImportImageModal from './ImportImageModal'
import AddImageModal from './AddImageModal'
import CreateButton from 'core/components/buttons/CreateButton'
import { SessionState, sessionStoreKey } from 'core/session/sessionReducers'
import { useSelector } from 'react-redux'
import PollingData from 'core/components/PollingData'
import SimpleLink from 'core/components/SimpleLink'
import DeleteVmSnapshot from './DeleteVmSnapshot'
import { TopHeaderActionPortal } from 'core/elements/header/portals'
import ExternalLink from 'core/components/ExternalLink'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import { makeStyles } from '@material-ui/styles'
import Theme from 'core/themes/model'
import { isOpenCloud, isReadonlyUser } from 'core/utils/helpers'
import TitleWithInfoIcon from 'core/components/TitleWithInfoIcon'
import ImageStatusInfo from './ImageStatusInfo'
import Text from 'core/elements/Text'
import Tooltip from 'core/elements/tooltip'
import useScrollPosition from 'core/hooks/useScrollPosition'
import { ImagePageTabs } from './model'
import { ImageDetailsPageTabs } from './details/model'
import { IVmDetailsPageTabs } from 'openstack/components/vms/vm-details/model'
import { IVolumeSnapshotDetailsPageTabs } from 'openstack/components/storage/snapshots/details/model'
import StringMultiDropdownFilter from 'core/elements/grid/filters/StringMultiDropdownFilter'
import { getVmSnapshotColumns, gridSearchFn } from 'app/plugins/openstack/components/images/helpers'
import { Labels } from 'k8s/components/common/entity/labels-and-annotations/LabelsOrAnnotations'
import DeleteImage from 'app/plugins/openstack/components/images/DeleteImage'

type ModelDataKey = DataKeys.Images
type SelectorModel = ArrayElement<ReturnType<typeof imagesSelector>>
type ActionParams = InferActionParams<typeof listImages>
// @fixme using a type here because of https://github.com/microsoft/TypeScript/issues/15300
type Params = ActionParams & {}

const defaultParams: Params = {
  orderBy: 'created_at',
  orderDirection: 'desc',
}

const usePrefParams = createUsePrefParamsHook<Params & TablePrefsParams>('Images', listTablePrefs)

export const TableHeaderTooltip = ({ message }) => {
  return <div>{message}</div>
}

const searchTargets = [
  'name',
  'id',
  'status',
  'visibility',
  'disk_format',
  'tags',
  'properties',
  'imageTypeDisplay',
]
const searchFunctions = [
  {
    property: 'properties',
    searchFn: (value, searchString) => gridSearchFn(value, searchString),
  },
]

export const TPMModelText = {
  'tpm-crb': 'CRB',
  'tpm-tis': 'TIS',
}

export const imageColumns = (imageListPage = true): GridViewColumn<SelectorModel>[] => [
  {
    key: 'name',
    label: 'Name',
    width: 'medium',
    CellComponent: createGridLinkCell({
      routeToFn: ({ id, name }) =>
        !!name
          ? routeManager.routes.openstack.imageDetails.path({
              id,
              detailsTab: ImageDetailsPageTabs.Overview,
            })
          : '',
    }),
  },
  {
    key: 'id',
    label: 'UUID',
    display: false,
  },
  {
    key: 'status',
    label: <TitleWithInfoIcon titleText="Status" tooltipMsg={<ImageStatusInfo />} />,
  },
  {
    key: 'imageTypeDisplay',
    label: 'Type',
  },
  {
    key: 'visibility',
    label: 'Visibility',
  },
  {
    key: 'protected',
    label: 'Protected',
    render: (val) => (val ? 'True' : 'False'),
  },
  {
    key: 'disk_format',
    label: 'Disk Format',
  },
  {
    key: 'virtual_size',
    label: (
      <TitleWithInfoIcon
        titleText="Virtual Disk Size"
        tooltipMsg={
          <TableHeaderTooltip message="The size of the VM disk that will get created from this snapshot" />
        }
      />
    ),
    render: humanReadableSize,
  },
  {
    key: 'size',
    label: (
      <TitleWithInfoIcon
        titleText="File Size"
        tooltipMsg={
          <TableHeaderTooltip message="The size of the snapshot file that is stored in the Image Library" />
        }
      />
    ),
    render: humanReadableSize,
  },
  {
    key: 'hw_tpm_model',
    label: 'Virtual TPM 2.0',
    render: (value, item) =>
      item?.hw_tpm_version === '2.0' ? `Enabled (${TPMModelText[value]})` : '',
  },
  {
    key: 'tags',
    label: 'Tags',
    render: (tags) => tags?.join(', '),
  },
  {
    key: 'properties',
    label: 'Properties',
    disableSorting: true,
    ...(imageListPage
      ? { CellComponent: createResourceLabelsCell({ type: 'table', separator: '=' }) }
      : {
          render: (value) => (
            <Labels
              labels={value}
              variant="default"
              separator={'='}
              containerType={'table'}
              maxVisible={1}
              showMoreButton={true}
            />
          ),
        }),
  },
  {
    key: 'created_at',
    label: 'Age',
    formatFn: (value) => durationBetweenDates({ labels: ['d', 'h'] })(value),
  },
]

// const dropdownFilters = [
//   {
//     key: 'imageTypeDisplay',
//     label: 'Type',
//     FilterComponent: StringMultiDropdownFilter,
//     filterComponentProps: {
//       label: 'Type',
//     },
//     filterComponentOptionsPropName: 'dropdownOptions',
//     getOptionsFn: () => {
//       return [
//         {
//           label: 'VM Ephemeral Disk Snapshot',
//           value: 'VM Ephemeral Disk Snapshot',
//         },
//         {
//           label: 'VM Volume Snapshot',
//           value: 'VM Volume Snapshot',
//         },
//       ]
//     },
//     equalityComparerFn: (item, values) => {
//       return values?.includes(item?.imageTypeDisplay)
//     },
//   },
// ]

export default function ImagesListPage() {
  const { allParams: params, getParamsUpdater } = useGlobalParams(usePrefParams, defaultParams)
  const { history, location, match } = useReactRouter()
  const { tab } = match.params

  const session = useSelector(prop<string, SessionState>(sessionStoreKey))
  const { features } = session
  const classes = useStyles()
  const isVmware = features?.experimental?.pmov2_du_type === 'vmware'
  const scrollY = useScrollPosition()

  const offset = useMemo(() => {
    return { vertical: 8 + scrollY, horizontal: 0 }
  }, [scrollY])

  const [selectedImage, setSelectedImage] = useState<SelectorModel>(null)
  const [showEditImageModal, setShowEditImageModal] = useState<boolean>(false)
  const [showEditMetadataModal, setShowEditMetadataModal] = useState<boolean>(false)
  const [needToValidateCert, setNeedToValidateCert] = useState<boolean>(false)
  const [glanceHostNotReachable, setGlanceHostNotReachable] = useState<boolean>(false)
  const [hostEndpoint, setHostEndpoint] = useState('')

  const { message, loading, reload, refreshing: imagesRefreshing } = useListAction(listImages, {
    params,
  })
  const data = useAppSelector(imagesSelector)

  const images = useMemo(() => {
    return data.filter((image) => image?.imageType === 'image')
  }, [data])
  const vmSnapshots = useMemo(() => {
    return data.filter((image) =>
      ['ephemeralDiskSnapshot', 'vmVolumeSnapshot'].includes(image?.imageType),
    )
  }, [data])

  const { loading: loadingVms } = useListAction(listVirtualMachinesLite)
  const vmsById = useSelector(virtualMachinesLiteByIdSelector)

  const { loading: loadingVolumeSnapshots } = useListAction(listVolumeSnapshots)
  const volumeSnapshotsById = useSelector(volumeSnapshotsByIdSelector)

  const asyncCall = useCallback(async () => {
    try {
      const endpoint = await getHostGlanceEndpoint()
      setHostEndpoint(endpoint)
      await testHostGlance(endpoint)

      setGlanceHostNotReachable(false)
      setNeedToValidateCert(false)
    } catch (err) {
      if (err?.err?.message === 'Network Error') {
        setNeedToValidateCert(true)
      } else if (err?.err?.code === 'ECONNABORTED') {
        setGlanceHostNotReachable(true)
      }
    }
  }, [getHostGlanceEndpoint, testHostGlance])

  useEffect(() => {
    asyncCall()
  }, [asyncCall])

  const vmSnapshotColumns: GridViewColumn<SelectorModel>[] = getVmSnapshotColumns({
    vmsById,
    volumeSnapshotsById,
  })

  const batchActions: GridBatchActionSpec<SelectorModel>[] = [
    {
      label: 'Edit',
      icon: 'edit',
      cond: (selectedItems) => selectedItems.length === 1,
      disabledTooltip: 'You can only edit one image at a time',
      handleAction: ([image]) => {
        setSelectedImage(image)
        setShowEditImageModal(true)
      },
      refreshAfterSuccess: true,
    },
    {
      cond: (selectedItems) => selectedItems.length === 1 && selectedItems.every((item) => !!item),
      disabledTooltip: 'You can only edit properties for a single image at a time',
      label: 'Edit Properties',
      icon: 'tags',
      handleAction: ([image]) => {
        setSelectedImage(image)
        setShowEditMetadataModal(true)
      },
      refreshAfterSuccess: true,
    },
  ]

  const hideImageUploadButton = isOpenCloud()

  return (
    <>
      <DocumentMeta title="Images" />
      <PollingData
        hidden
        loading={loading}
        onReload={() => {
          return new Promise<void>((resolve) => {
            reload(true, false)
            asyncCall()
            resolve()
          })
        }}
        refreshDuration={1000 * 30}
      />
      <TopHeaderActionPortal>
        {needToValidateCert ? (
          <Tooltip
            message="Trust Image Library Certificate"
            align={{
              vertical: 'bottom',
              horizontal: 'middle',
            }}
          >
            <ExternalLink className={classes.warning} url={hostEndpoint} textVariant="subtitle2">
              <FontAwesomeIcon className={classes.icon}>exclamation-circle</FontAwesomeIcon>
              Action Required
            </ExternalLink>
          </Tooltip>
        ) : glanceHostNotReachable ? (
          <Text variant="subtitle2" className={classes.warning}>
            <FontAwesomeIcon className={classes.icon}>exclamation-circle</FontAwesomeIcon>
            Image Library Host Not Reachable
          </Text>
        ) : null}
      </TopHeaderActionPortal>
      <ImportImageModal addRoute={routeManager.routes.openstack.importImage} />
      {!isVmware && <AddImageModal addRoute={routeManager.routes.openstack.addImage} />}
      {showEditImageModal && (
        <EditImageModal onClose={() => setShowEditImageModal(false)} rows={[selectedImage]} />
      )}
      {showEditMetadataModal && (
        <EditImageMetadataModal
          onClose={() => setShowEditMetadataModal(false)}
          rows={[selectedImage]}
        />
      )}
      <Tabs route={routeManager.routes.openstack.images}>
        <Tab label="Images" value={ImagePageTabs.Images}>
          <ListContainer<ModelDataKey, SelectorModel>
            dataKey={DataKeys.Images}
            searchTargets={searchTargets}
            searchFunctions={searchFunctions}
            uniqueIdentifier="id"
            loading={loading}
            loadingMessage={message}
            onRefresh={() => {
              reload(true)
              asyncCall()
            }}
            refreshing={imagesRefreshing}
            data={images}
            columns={imageColumns(true)}
            addUrl={
              hideImageUploadButton || isVmware
                ? null
                : routeManager.routes.openstack.addImage.path({ tab })
            }
            addText={hideImageUploadButton || isVmware ? null : 'Add Image'}
            getParamsUpdater={getParamsUpdater}
            batchActions={batchActions}
            deleteAction={deleteImage}
            deleteCond={(selectedItems) => !selectedItems?.some((item) => item?.protected)}
            deleteDisabledInfo="Unable to delete protected images"
            DeleteDialogComponent={DeleteImage}
            multiSelection
            extraHeaderContent={
              hideImageUploadButton || isReadonlyUser() ? null : (
                <CreateButton
                  variant="secondary"
                  onClick={() =>
                    history.push(routeManager.routes.openstack.importImage.path({ tab }))
                  }
                >
                  Import With CLI
                </CreateButton>
              )
            }
            {...pick(listTablePrefs, params)}
          />
        </Tab>
        <Tab label="VM Snapshots" value={ImagePageTabs.Snapshots}>
          <ListContainer<ModelDataKey, SelectorModel>
            dataKey={DataKeys.Images}
            label="VM Snapshots"
            searchTargets={searchTargets}
            searchFunctions={searchFunctions}
            uniqueIdentifier="id"
            loading={loading}
            loadingMessage={message}
            onRefresh={() => {
              reload(true)
              asyncCall()
            }}
            refreshing={imagesRefreshing}
            data={vmSnapshots}
            columns={vmSnapshotColumns}
            addUrl={
              hideImageUploadButton || isVmware
                ? null
                : routeManager.routes.openstack.addImage.path({ tab })
            }
            addText={hideImageUploadButton || isVmware ? null : 'Add Image'}
            getParamsUpdater={getParamsUpdater}
            batchActions={batchActions}
            deleteAction={deleteImage}
            deleteCond={(selectedItems) => !selectedItems?.some((item) => item?.protected)}
            deleteDisabledInfo="Unable to delete protected images"
            DeleteDialogComponent={DeleteVmSnapshot}
            multiSelection
            // dropdownFilters={dropdownFilters}
            extraHeaderContent={
              <>
                <ExternalLink url="https://platform9.com/docs/private-cloud-director/private-cloud-director/image-library---images#images-vs-vm-snapshots">
                  Read About Image Types
                </ExternalLink>
                {hideImageUploadButton || isReadonlyUser() ? null : (
                  <CreateButton
                    variant="secondary"
                    onClick={() =>
                      history.push(routeManager.routes.openstack.importImage.path({ tab }))
                    }
                  >
                    Import With CLI
                  </CreateButton>
                )}
              </>
            }
            {...pick(listTablePrefs, params)}
          />
        </Tab>
      </Tabs>
    </>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  icon: {
    color: theme.components.badge.error.color,
    marginRight: 8,
  },
  warning: {
    color: theme.components.badge.error.color,
    '& > span': {
      color: theme.components.badge.error.color,
    },
  },
}))
